import type { SpaceSharing, SpaceSummary } from '@doriavboard/shared';
import { useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import { spacesApi } from '../api/endpoints';
import { toast } from '../stores/toasts';
import { PeopleWithAccess } from './PeopleWithAccess';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';

interface SpaceShareDialogProps {
  space: SpaceSummary;
  open: boolean;
  onOpenChange(open: boolean): void;
  /** Called after people were added, changed or removed. */
  onChanged?(): void;
}

export function SpaceShareDialog({ space, open, onOpenChange, onChanged }: SpaceShareDialogProps) {
  const [sharing, setSharing] = useState<SpaceSharing | null>(null);

  useEffect(() => {
    if (!open) return;
    setSharing(null);
    spacesApi
      .sharing(space.id)
      .then(setSharing)
      .catch((err) => toast(errorMessage(err), 'error'));
  }, [open, space.id]);

  async function invite(username: string, role: 'editor' | 'viewer') {
    setSharing(await spacesApi.invite(space.id, username, role));
    toast('Added to the space', 'success');
    onChanged?.();
  }

  async function changeRole(userId: string, next: 'editor' | 'viewer') {
    try {
      setSharing(await spacesApi.setMemberRole(space.id, userId, next));
      onChanged?.();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function remove(userId: string) {
    try {
      await spacesApi.removeMember(space.id, userId);
      setSharing((s) => s && { ...s, members: s.members.filter((m) => m.user.id !== userId) });
      onChanged?.();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Share “${space.name}”`}
      description="People in a space can open every board in it: editors can also change them and add new ones, viewers can only look."
      size="lg"
    >
      <PeopleWithAccess
        people={sharing}
        canManage={space.role === 'owner'}
        onInvite={invite}
        onChangeRole={changeRole}
        onRemove={remove}
      />
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      </div>
    </Modal>
  );
}
